import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

import {
  type OrderingCart,
  sumLedgerTotal,
  type WantedTime,
} from '@order/features/ordering';
import { StripeLogger } from '@order/integrations/stripe';
import { useTelemetry } from '@order/Telemetry';

import { useNotificationType } from '../../../../integrations';
import { getDeliveryParams } from '../../utils';
import { useCreatePaymentIntent } from '../useCreatePaymentIntent';

export const usePaymentIntentCreation = (
  props: UsePaymentIntentCreationParams,
) => {
  const {
    wantedTime,
    selectedTip,
    cart,
    availableCredit,
    shouldUseCredit,
    dropoffLocation,
    shouldIncludeUtensils,
    changePaymentMethod,
    cancelRequest,
  } = props;

  const { track } = useTelemetry();

  const getNotificationType = useNotificationType();
  const createPaymentIntentMutation = useCreatePaymentIntent();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const createPaymentIntent = useCallback(
    async (
      paymentMethodId: string,
      paymentMethodType: string,
      shouldSetupFutureUsage?: boolean,
    ) => {
      try {
        const {
          orderId = '',
          orderChannel = 'pickup',
          restaurantId = '',
          deliveryOrderDetail,
          canTrackOrderStatus,
          ledger,
        } = cart;

        const wantedTimeString = wantedTime?.time ?? '';
        const deliveryParams = getDeliveryParams(orderChannel, selectedTip, deliveryOrderDetail); // prettier-ignore
        const notificationType = getNotificationType({ canTrackOrderStatus, wantedTime: wantedTimeString }).notificationType ?? ''; // prettier-ignore
        const amount = sumLedgerTotal(ledger, selectedTip, availableCredit, shouldUseCredit).total; // prettier-ignore

        const params = {
          paymentMethodId,
          amount,
          orderId,
          orderType: OrderTypeValues.GravyOrder,
          restaurantEntityId: restaurantId,
          deliveryOrderDetail: deliveryParams,
          dropoffLocationId: dropoffLocation,
          includeUtensils: shouldIncludeUtensils,
          notificationType,
          pickupTip: selectedTip,
          useCredit: shouldUseCredit,
          wantedTime: wantedTimeString,
          shouldSetupFutureUsage,
        };

        track('checkout.stripe-usage', { event: 'create_payment_intent', paymentMethodType, params }); // prettier-ignore

        const response = await createPaymentIntentMutation(params);
        const { paymentIntentId, clientSecret } = response;

        changePaymentMethod(paymentIntentId);

        track('checkout.stripe-usage', { event: 'confirm_payment_intent', paymentIntentId }); // prettier-ignore
        return clientSecret;
      } catch (error) {
        StripeLogger.logGenericError(error as Error);
        cancelRequest(true);
        track('checkout.stripe-usage', { event: 'stripe_failure', error }); // prettier-ignore
        addNoticeBanner({
          text: (error as Error).message,
          palette: 'caution',
        });
      }
    },
    [
      addNoticeBanner,
      availableCredit,
      cancelRequest,
      cart,
      changePaymentMethod,
      createPaymentIntentMutation,
      dropoffLocation,
      getNotificationType,
      selectedTip,
      shouldIncludeUtensils,
      shouldUseCredit,
      track,
      wantedTime?.time,
    ],
  );

  return { createPaymentIntent };
};

type UsePaymentIntentCreationParams = {
  wantedTime: WantedTime | undefined;
  selectedTip: number;
  cart: OrderingCart;
  availableCredit: number;
  shouldUseCredit: boolean;
  dropoffLocation: string | undefined;
  shouldIncludeUtensils: boolean;
  changePaymentMethod: (paymentMethodId: string) => void;
  cancelRequest: (shouldCancel: boolean) => void;
};
